import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { UtilisateurService } from './utilisateur.service';
import {
  RapportParcoursFormationResponse,
  RapportUtilisateurParcoursResponse,
  RapportContenuParcoursResponse
} from 'src/models/rapport/rapport-parcours-formation.model';

@Injectable({
  providedIn: 'root'
})
export class RapportParcoursFormationService {

  private baseUrl = environment.api_LMS + 'RapportParcoursFormation';

  constructor(
    private http: HttpClient,
    private utilisateurService: UtilisateurService,
    private translateService: TranslateService
  ) {}

  private get entiteId(): number {
    return this.utilisateurService.entiteUtilisateurSelectionnee.entiteId;
  }

  private get utilisateurId(): number {
    return this.utilisateurService.entiteUtilisateurSelectionnee.utilisateurId;
  }

  private get codeLangue(): string {
    return this.translateService.currentLang || 'fr';
  }

  obtenirRapportParcours(): Observable<RapportParcoursFormationResponse> {
    return this.http.get<RapportParcoursFormationResponse>(`${this.baseUrl}/ObtenirRapportParcours`, {
      params: {
        entiteId: this.entiteId,
        utilisateurId: this.utilisateurId,
        codeLangue: this.codeLangue
      }
    });
  }

  obtenirRapportUtilisateurs(parcoursFormationId?: number): Observable<RapportUtilisateurParcoursResponse> {
    const params: any = {
      entiteId: this.entiteId,
      utilisateurId: this.utilisateurId,
      codeLangue: this.codeLangue
    };
    if (parcoursFormationId != null) params.parcoursFormationId = parcoursFormationId;

    return this.http.get<RapportUtilisateurParcoursResponse>(`${this.baseUrl}/ObtenirRapportUtilisateurs`, { params });
  }

  obtenirRapportContenus(parcoursFormationId?: number, utilisateurFiltreId?: number): Observable<RapportContenuParcoursResponse> {
    const params: any = {
      entiteId: this.entiteId,
      utilisateurId: this.utilisateurId,
      codeLangue: this.codeLangue
    };
    if (parcoursFormationId != null) params.parcoursFormationId = parcoursFormationId;
    if (utilisateurFiltreId != null) params.utilisateurFiltreId = utilisateurFiltreId;

    return this.http.get<RapportContenuParcoursResponse>(`${this.baseUrl}/ObtenirRapportContenus`, { params });
  }

  exporterParcours(): Observable<Blob> {
    return this.http.post(`${this.baseUrl}/ExporterParcours`, {
      entiteId: this.entiteId,
      utilisateurId: this.utilisateurId,
      codeLangue: this.codeLangue
    }, { responseType: 'blob' });
  }

  exporterParcoursPdf(): Observable<Blob> {
    return this.http.post(`${this.baseUrl}/ExporterParcoursPdf`, {
      entiteId: this.entiteId,
      utilisateurId: this.utilisateurId,
      codeLangue: this.codeLangue
    }, { responseType: 'blob' });
  }

  exporterUtilisateurs(parcoursFormationId?: number, ligneIds?: { parcoursFormationId: number; utilisateurId: number }[]): Observable<Blob> {
    return this.http.post(`${this.baseUrl}/ExporterUtilisateurs`, {
      entiteId: this.entiteId,
      utilisateurId: this.utilisateurId,
      codeLangue: this.codeLangue,
      parcoursFormationId: parcoursFormationId ?? null,
      ligneIds: ligneIds ?? []
    }, { responseType: 'blob' });
  }

  exporterUtilisateursPdf(parcoursFormationId?: number, ligneIds?: { parcoursFormationId: number; utilisateurId: number }[]): Observable<Blob> {
    return this.http.post(`${this.baseUrl}/ExporterUtilisateursPdf`, {
      entiteId: this.entiteId,
      utilisateurId: this.utilisateurId,
      codeLangue: this.codeLangue,
      parcoursFormationId: parcoursFormationId ?? null,
      ligneIds: ligneIds ?? []
    }, { responseType: 'blob' });
  }

  exporterContenus(parcoursFormationId?: number, utilisateurFiltreId?: number, ligneIds?: { parcoursFormationId: number; scormPackageId: number; utilisateurId: number }[]): Observable<Blob> {
    return this.http.post(`${this.baseUrl}/ExporterContenus`, {
      entiteId: this.entiteId,
      utilisateurId: this.utilisateurId,
      codeLangue: this.codeLangue,
      parcoursFormationId: parcoursFormationId ?? null,
      utilisateurFiltreId: utilisateurFiltreId ?? null,
      ligneIds: ligneIds ?? []
    }, { responseType: 'blob' });
  }

  exporterContenusPdf(parcoursFormationId?: number, utilisateurFiltreId?: number, ligneIds?: { parcoursFormationId: number; scormPackageId: number; utilisateurId: number }[]): Observable<Blob> {
    return this.http.post(`${this.baseUrl}/ExporterContenusPdf`, {
      entiteId: this.entiteId,
      utilisateurId: this.utilisateurId,
      codeLangue: this.codeLangue,
      parcoursFormationId: parcoursFormationId ?? null,
      utilisateurFiltreId: utilisateurFiltreId ?? null,
      ligneIds: ligneIds ?? []
    }, { responseType: 'blob' });
  }
}
